function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function safeStringify(value: unknown): string | null {
  try {
    const json = JSON.stringify(value);
    return json && json !== '{}' ? json : null;
  } catch {
    return null;
  }
}

/**
 * Convert thrown values and protocol error payloads into user-safe text.
 *
 * WebSocket protocol errors arrive as plain objects. Rendering them with
 * String(error) produces "[object Object]", so prefer common message fields
 * and fall back to JSON only when needed.
 */
export function getErrorMessage(error: unknown, fallback = 'Unknown error'): string {
  if (error instanceof Error) {
    return error.message || fallback;
  }

  if (typeof error === 'string') {
    return error.trim() || fallback;
  }

  if (error == null) {
    return fallback;
  }

  if (isRecord(error)) {
    const message = error.message ?? error.error ?? error.reason;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
    if (message !== undefined && message !== error) {
      return getErrorMessage(message, fallback);
    }

    const json = safeStringify(error);
    if (json) return json;
  }

  const text = String(error);
  return text && text !== '[object Object]' ? text : fallback;
}
